/**
 * Cel-banded toon atmosphere — replaces the smooth Fresnel halo with
 * discrete glow rings to match the rest of the cartoon language.
 *
 * Rather than a continuous fresnel falloff, the fragment shader quantizes
 * the rim factor into N bands, producing the "stacked outline rings"
 * look that reads as illustrated rather than physically rendered.
 *
 * Usage is interchangeable with createAtmosphere — same caller API,
 * with bandCount instead of power.
 */

import {
  AdditiveBlending,
  BackSide,
  Color,
  Mesh,
  ShaderMaterial,
  SphereGeometry,
} from "three";

const TOON_ATMOSPHERE_VERT = /* glsl */ `
  varying vec3 vNormal;
  varying vec3 vViewDir;

  void main() {
    vNormal = normalize(normalMatrix * normal);
    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
    vViewDir = normalize(-mvPosition.xyz);
    gl_Position = projectionMatrix * mvPosition;
  }
`;

const TOON_ATMOSPHERE_FRAG = /* glsl */ `
  uniform vec3 uColor;
  uniform float uIntensity;
  uniform float uBandCount;
  varying vec3 vNormal;
  varying vec3 vViewDir;

  void main() {
    float rim = 1.0 - max(0.0, dot(normalize(vNormal), normalize(vViewDir)));
    // Quantize to N discrete bands so the halo reads as cel-shaded
    // rings rather than smooth falloff. Inner bands get a hint of
    // residual smoothing so the silhouette stays crisp without a
    // mach-band artifact at the equator.
    float bands = max(uBandCount, 1.0);
    float quantized = floor(rim * bands) / bands;
    float feather = smoothstep(0.0, 1.0 / bands, fract(rim * bands)) * (0.18 / bands);
    float alpha = clamp((quantized + feather) * uIntensity, 0.0, 1.0);
    // Boost alpha toward the silhouette edge so the outermost ring
    // pops — that's the "atmosphere glow" cue.
    alpha = pow(alpha, 0.85);
    gl_FragColor = vec4(uColor, alpha);
  }
`;

export interface ToonAtmosphereOptions {
  planetRadius: number;
  color: number;
  /** Outer ring opacity multiplier. */
  intensity?: number;
  /** Number of discrete cel rings (typical 2..5). */
  bandCount?: number;
  /** Shell scale relative to planet radius. */
  shellScale?: number;
}

export function createToonAtmosphere(opts: ToonAtmosphereOptions): Mesh {
  const intensity = opts.intensity ?? 1.2;
  const bandCount = opts.bandCount ?? 3;
  const shellScale = opts.shellScale ?? 1.12;
  const geometry = new SphereGeometry(opts.planetRadius * shellScale, 48, 36);
  const material = new ShaderMaterial({
    uniforms: {
      uColor: { value: new Color(opts.color) },
      uIntensity: { value: intensity },
      uBandCount: { value: bandCount },
    },
    vertexShader: TOON_ATMOSPHERE_VERT,
    fragmentShader: TOON_ATMOSPHERE_FRAG,
    transparent: true,
    blending: AdditiveBlending,
    depthWrite: false,
    side: BackSide,
  });
  const mesh = new Mesh(geometry, material);
  mesh.name = "toon-atmosphere";
  return mesh;
}

export const __testing = { TOON_ATMOSPHERE_VERT, TOON_ATMOSPHERE_FRAG };
